import { Box, Button, Switch } from "@mantine/core";
import { IValueLabel, taskOrIssueENUM } from "@mctypes/global.types";
import {
  ICapitalLetter,
  IOtherAttributeConfigApi,
  IOtherAttributeConfigItemProps,
} from "@schemas/otherAttributesSchema";
import { ColDef, ICellRendererParams } from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useState } from "react";
import { AG_GRID_LOCALE_NOR } from "../../../agGridLocale";
import { sortArrayByNumber } from "../../../app/utils";
import { VSpace } from "../../index";
import OtherAttributeItemConfigModal from "./OtherAttributesConfigFormModal";

export type IOtherAttributeConfigItemPropsWithId = { id: string } & IOtherAttributeConfigItemProps;

export const TYPE_OF_VALUE = {
  STRING: "Tekst",
  MULTILINESTRING: "Tekst (flerlinje)",
  NUMBER: "Tall",
  DATE: "Dato",
  BOOLEAN: "Ja/nei",
  DROPDOWN: "Velg fra listen",
  FORMULA: "Formel",
  MATH_FORMULA: "Matematisk formel",
} as const;

const OtherAttributesConfig: React.FC<{
  otherAttributesConfig: IOtherAttributeConfigApi;
  onAdd: (otherAttributesConfigItemProps: IOtherAttributeConfigItemProps) => void;
  onEdit: (
    otherAttributeConfigItemProps: IOtherAttributeConfigItemProps,
    otherAttributeConfigKey: ICapitalLetter,
  ) => void;
  onUpdateOrder: (idsInOrder: ICapitalLetter[]) => void;
  taskOrIssue?: taskOrIssueENUM;
}> = ({ otherAttributesConfig, onAdd, onEdit, onUpdateOrder, taskOrIssue }) => {
  const [addModalOpen, setAddModalOpen] = useState<boolean>(false);
  const [showOnlyActive, setShowOnlyActive] = useState<boolean>(true);
  const [otherAttributesConfigItemPropsToEdit, setOtherAttributesConfigItemPropsToEdit] = useState<
    IOtherAttributeConfigItemPropsWithId | undefined
  >(undefined);

  const rows: IOtherAttributeConfigItemPropsWithId[] = Object.entries(otherAttributesConfig)?.map(([key, value]) => ({
    id: key,
    ...value,
  }));

  const filteredRows = sortArrayByNumber(showOnlyActive ? rows.filter((r) => r.isActive === true) : rows, "order");

  const columns: ColDef<IOtherAttributeConfigItemPropsWithId>[] = [
    { headerName: "Order", field: "order", hide: true },
    {
      headerName: "ID",
      width: 100,
      field: "id",
      rowDrag: true,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<IOtherAttributeConfigItemPropsWithId>) => {
        return (
          <Button
            color="mc-primary"
            size="sm"
            px={"sm"}
            variant="subtle"
            onClick={() => setOtherAttributesConfigItemPropsToEdit(params.data)}
          >
            <b>{params.value}</b>
          </Button>
        );
      },
    },
    {
      headerName: "Tittel",
      field: "label",
      sortable: false,
      cellRenderer: (params) => {
        return (
          <div className="d-flex align-items-center" style={{ marginLeft: "-15px" }}>
            <div
              className="mmi-dot mr-2"
              style={{
                backgroundColor: params.data?.labelColor,
              }}
            ></div>
            <span>{params.value}</span>
          </div>
        );
      },
    },
    {
      headerName: "Type verdi",
      field: "typeOfValue",
      sortable: false,
      cellRenderer: (params: ICellRendererParams<IOtherAttributeConfigItemPropsWithId>) => {
        return <span>{TYPE_OF_VALUE[params.value]}</span>;
      },
    },
    { headerName: "Seksjon", field: "section", sortable: false },
    { headerName: "Beskrivelse", field: "description", sortable: false, flex: 1 },
    {
      headerName: "Låst",
      field: "locked",
      width: 100,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<IOtherAttributeConfigItemPropsWithId>) => {
        return (
          <span>
            {params.value ? (
              <i className="fa fa-check" style={{ color: "green" }} />
            ) : (
              <i className="fa fa-close" style={{ color: "red" }} />
            )}
          </span>
        );
      },
    },
    {
      headerName: "Er aktiv",
      field: "isActive",
      width: 100,
      sortable: false,
      cellRenderer: (params: ICellRendererParams<IOtherAttributeConfigItemPropsWithId>) => {
        return (
          <span>
            {params.value ? (
              <i className="fa fa-check" style={{ color: "green" }} />
            ) : (
              <i className="fa fa-close" style={{ color: "red" }} />
            )}
          </span>
        );
      },
    },
  ];

  const _onAdd = (otherAttributesConfigItemProps: IOtherAttributeConfigItemProps) => {
    onAdd(otherAttributesConfigItemProps);
    setAddModalOpen(false);
  };

  const _onEdit = (otherAttributesConfigItemProps: IOtherAttributeConfigItemProps) => {
    setOtherAttributesConfigItemPropsToEdit(null);
    onEdit(otherAttributesConfigItemProps, otherAttributesConfigItemPropsToEdit?.id as ICapitalLetter);
  };

  const onRowDragEnd = (event) => {
    const rows = event.api?.getRenderedNodes();
    const ids = rows?.map((row) => row.data.id);
    onUpdateOrder(ids as ICapitalLetter[]);
  };

  const existingSections = Array.from(new Set(Object.values(otherAttributesConfig ?? {}).map((item) => item.section)));
  return (
    <Box>
      {addModalOpen && (
        <OtherAttributeItemConfigModal
          orignalOtherAttributesConfigItemProps={otherAttributesConfigItemPropsToEdit}
          onClose={() => setAddModalOpen(false)}
          onSave={_onAdd}
          title="Legg til ny attributt"
          addOrEdit="add"
          existingSections={existingSections}
          taskOrIssue={taskOrIssue}
        />
      )}
      {otherAttributesConfigItemPropsToEdit && (
        <OtherAttributeItemConfigModal
          orignalOtherAttributesConfigItemProps={otherAttributesConfigItemPropsToEdit}
          onClose={() => setOtherAttributesConfigItemPropsToEdit(null)}
          onSave={_onEdit}
          title={`Rediger attributt ${otherAttributesConfigItemPropsToEdit.id}`}
          addOrEdit="edit"
          existingSections={existingSections}
          taskOrIssue={taskOrIssue}
        />
      )}
      <div className="d-flex align-items-center mb-2" style={{ gap: "10px" }}>
        <Switch
          checked={showOnlyActive}
          onChange={(e) => setShowOnlyActive(e.currentTarget.checked)}
          label="Vis kun aktive"
        />
      </div>

      <div className="ag-theme-quartz h-100" id="myGrid">
        <AgGridReact
          domLayout="autoHeight"
          rowData={filteredRows}
          columnDefs={columns}
          suppressClickEdit={true}
          animateRows={true}
          suppressContextMenu={true}
          localeText={AG_GRID_LOCALE_NOR}
          suppressMoveWhenRowDragging={true}
          suppressCellFocus={true}
          rowDragManaged={true}
          onRowDragEnd={onRowDragEnd}
          defaultColDef={{ suppressHeaderMenuButton: true }}
        />
      </div>
      <VSpace />
      <Button px="xs" size="sm" color="green" onClick={() => setAddModalOpen(true)}>
        <i className="fa fa-plus fa-fw" />
      </Button>
    </Box>
  );
};

export default OtherAttributesConfig;

export const TYPE_OF_VALUE_OPTIONS: IValueLabel[] = Object.keys(TYPE_OF_VALUE).map((key) => ({
  value: key,
  label: TYPE_OF_VALUE[key],
}));
